import { useEffect, useState } from "react";
import {
    AlertCircle,
    DollarSign,
    ExternalLink,
    Layers,
    MessageCircle,
    MousePointerClick,
    Play,
    Radio,
    Send,
    Users,
} from "lucide-react";
import { useBroadcastsList } from "../api/queries";
import { useDashboardStore } from "../store/dashboardStore";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { SectionCard } from "../components/SectionCard";
import { KpiCard } from "../components/KpiCard";
import { Segmented } from "../components/Segmented";
import { Paginator } from "../components/Paginator";
import { DashSkeleton, SkelText } from "../components/Skeleton";
import { ApiBanner } from "../components/ApiBanner";
import { cur, deltaDir, fmt, fmtDelta, fmtPct } from "../lib/format";
import { cn } from "@/lib/utils";
import type { BroadcastItem } from "../types/reports";

type GroupBy = "template" | "none";

export function BroadcastsView() {
    const setView = useDashboardStore((s) => s.setView);
    const filters = useDashboardStore((s) => s.filters);
    const filterKey = JSON.stringify(filters);

    const [page, setPage] = useState(1);
    const [groupBy, setGroupBy] = useState<GroupBy>("template");
    const pageSize = 24;

    // Reset to page 1 when filters or grouping change
    useEffect(() => {
        setPage(1);
    }, [filterKey, groupBy]);

    const { data, error, isLoading, isFetching, refetch } = useBroadcastsList(
        page,
        pageSize,
        groupBy
    );
    const loading = isLoading && !data;

    const hero = (
        <PageHeader
            icon={Send}
            title="Broadcasts"
            subtitle="Disparos manuais"
        />
    );

    if (error && !data) {
        return (
            <PageShell hero={hero}>
                <ApiBanner error={error} onRetry={() => refetch()} />
            </PageShell>
        );
    }

    const kpis = data?.kpis;
    const items: BroadcastItem[] = data?.items ?? [];
    const pagination = data?.pagination;
    const totalItems = pagination?.total ?? items.length;
    const totalPages = pagination?.totalPages ?? 1;

    // Aggregate fallbacks when KPIs not provided
    // KPIs do UNIVERSO inteiro (não da página/agrupamento atual).
    // Backend manda `totalShots`/`totalSends`/`avgCtr` — esses são FIXOS
    // entre "Agrupar idênticos" e "Por disparo". Fallback pra soma da
    // página só rola quando o backend não retorna o campo (legado).
    const sumSends = items.reduce((s, b) => s + (b.sends || 0), 0);
    const kpiShots =
        kpis?.totalShots ?? kpis?.broadcasts ?? totalItems;
    const kpiSends =
        kpis?.totalSends != null ? kpis.totalSends : cur(kpis?.sends, sumSends);
    const kpiCtr = cur(kpis?.avgCtr ?? kpis?.ctr);
    const grouped = groupBy === "template";

    return (
        <PageShell hero={hero}>
            {/* KPI strip */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <KpiCard
                    icon={DollarSign}
                    iconColor="text-emerald-500"
                    label="Receita"
                    value={
                        loading ? (
                            <SkelText />
                        ) : (
                            "$" +
                            Number(cur(kpis?.revenue)).toLocaleString("pt-BR", {
                                maximumFractionDigits: 2,
                            })
                        )
                    }
                    delta={fmtDelta(kpis?.revenue)}
                    deltaDir={deltaDir(kpis?.revenue)}
                />
                <KpiCard
                    icon={Send}
                    iconColor="text-blue-500"
                    label="Disparos"
                    value={loading ? <SkelText /> : fmt(kpiShots)}
                    sub="no período"
                />
                <KpiCard
                    icon={Users}
                    iconColor="text-violet-500"
                    label="Envios"
                    value={loading ? <SkelText /> : fmt(Math.round(kpiSends))}
                />
                <KpiCard
                    icon={MousePointerClick}
                    iconColor="text-amber-500"
                    label="CTR médio"
                    value={loading ? <SkelText /> : fmtPct(kpiCtr)}
                    delta={fmtDelta(kpis?.avgCtr ?? kpis?.ctr)}
                    deltaDir={deltaDir(kpis?.avgCtr ?? kpis?.ctr)}
                />
            </div>

            {/* Grouping toggle */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Layers className="w-4 h-4 text-gray-400" />
                    {grouped ? (
                        <span>
                            Broadcasts idênticos{" "}
                            <strong className="text-gray-700">
                                agrupados por conteúdo
                            </strong>{" "}
                            — métricas somadas entre páginas
                        </span>
                    ) : (
                        <span>
                            Mostrando{" "}
                            <strong className="text-gray-700">
                                cada disparo individual
                            </strong>{" "}
                            (1 por página)
                        </span>
                    )}
                </div>
                <Segmented<GroupBy>
                    value={groupBy}
                    onChange={setGroupBy}
                    options={[
                        { v: "template", l: "Agrupar idênticos" },
                        { v: "none", l: "Por disparo" },
                    ]}
                />
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <DashSkeleton key={i} h={320} />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <SectionCard icon={Send} title="Broadcasts">
                    <EmptyMsg />
                </SectionCard>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch auto-rows-fr">
                        {items.map((b) => (
                            <BroadcastListCard
                                key={b.id}
                                b={b}
                                onClick={() => {
                                    const isGrouped =
                                        !!b.contentHash &&
                                        (b.groupedIds?.length ?? 0) > 1;
                                    if (isGrouped) {
                                        setView(
                                            {
                                                name: "broadcast",
                                                id: b.id,
                                                contentHash: b.contentHash,
                                                groupedIds: b.groupedIds,
                                            },
                                            b.name
                                        );
                                    } else {
                                        setView(
                                            { name: "broadcast", id: b.id },
                                            b.name
                                        );
                                    }
                                }}
                            />
                        ))}
                    </div>
                    {totalPages > 1 && (
                        <Paginator
                            page={page}
                            totalPages={totalPages}
                            totalItems={totalItems}
                            pageSize={pageSize}
                            onChange={setPage}
                            loading={isFetching}
                        />
                    )}
                </>
            )}
        </PageShell>
    );
}

function EmptyMsg() {
    return (
        <div className="text-center py-8">
            <AlertCircle className="w-6 h-6 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Sem dados no período</p>
        </div>
    );
}

interface BroadcastListCardProps {
    b: BroadcastItem;
    onClick: () => void;
}

function BroadcastListCard({ b, onClick }: BroadcastListCardProps) {
    const previewUrl = b.previewMediaUrl || b.mediaUrl || null;
    const mediaType = b.media;
    const isVideo = mediaType === "video";
    const hasImage = !!previewUrl && !isVideo;
    const isFromEsteira = !!(
        b.esteiraId ||
        b.esteiraName ||
        b.sourceType === "esteira"
    );
    const sourceLabel = isFromEsteira ? b.esteiraName || "Esteira" : "Manual";
    const pagesCount = b.pagesCount ?? (b.groupedIds?.length ?? 1);
    const isGroupedCard = pagesCount > 1;

    // CTR: hide when null/0 — grid collapses to 2 cols
    const ctrVal =
        b.ctr != null && b.ctr > 0
            ? b.ctr
            : b.sends > 0 && b.clicks > 0
              ? (b.clicks / b.sends) * 100
              : null;
    const ctrColor =
        ctrVal == null
            ? "bg-gray-100 text-gray-400"
            : ctrVal >= 8
              ? "bg-emerald-50 text-emerald-700"
              : ctrVal >= 6
                ? "bg-amber-50 text-amber-700"
                : ctrVal >= 3
                  ? "bg-blue-50 text-blue-700"
                  : "bg-gray-100 text-gray-600";

    return (
        <button
            onClick={onClick}
            className="text-left bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-md hover:border-blue-200 transition-all flex flex-col cursor-pointer group h-full"
        >
            {/* Hero media area — fixed aspect ratio */}
            <div
                className="relative bg-slate-100 shrink-0"
                style={{ aspectRatio: "4/3" }}
            >
                {hasImage ? (
                    <img
                        src={previewUrl!}
                        alt=""
                        className="object-cover w-full h-full"
                        onError={(e) => {
                            const img = e.currentTarget;
                            img.style.display = "none";
                            img.parentElement?.classList.add(
                                "bg-gradient-to-br",
                                "from-slate-200",
                                "to-slate-300"
                            );
                        }}
                    />
                ) : isVideo ? (
                    <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                            <Play className="w-6 h-6 text-white" />
                        </div>
                    </div>
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                        <MessageCircle className="w-8 h-8 text-slate-300" />
                    </div>
                )}

                {/* Source badge — top-left */}
                <div className="absolute top-2 left-2 flex flex-wrap gap-1.5">
                    {isFromEsteira ? (
                        <span className="inline-flex items-center gap-1 bg-violet-600/95 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md shadow-sm">
                            <Radio className="w-3 h-3" />
                            {sourceLabel}
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 bg-blue-600/95 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md shadow-sm">
                            <Send className="w-3 h-3" />
                            Manual
                        </span>
                    )}
                    {isGroupedCard && (
                        <span className="inline-flex items-center gap-1 bg-amber-500/95 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md shadow-sm">
                            <Layers className="w-3 h-3" />
                            {pagesCount} páginas
                        </span>
                    )}
                </div>

                {/* Revenue chip — top-right */}
                <div className="absolute top-2 right-2 inline-flex items-center gap-1 bg-white/95 backdrop-blur-sm text-gray-800 text-[10px] font-semibold px-2 py-1 rounded-md shadow-sm">
                    <DollarSign className="w-3 h-3 text-emerald-500" />
                    $
                    {Number(b.revenue).toLocaleString("pt-BR", {
                        maximumFractionDigits: 2,
                    })}
                </div>

                {/* Bottom copy overlay */}
                {(b.previewTitle || b.previewText) && (
                    <div className="absolute inset-x-0 bottom-0 p-2.5 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                        <div className="bg-white/95 backdrop-blur-sm rounded-lg px-2.5 py-1.5 shadow-sm">
                            {b.previewTitle ? (
                                <>
                                    <p className="text-[11px] font-bold text-gray-900 leading-snug line-clamp-1">
                                        {b.previewTitle}
                                    </p>
                                    {b.previewSubtitle && (
                                        <p className="text-[10px] text-gray-600 leading-snug line-clamp-1 mt-0.5">
                                            {b.previewSubtitle}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <p className="text-[11px] text-gray-800 leading-snug line-clamp-2">
                                    {b.previewText}
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Body — flex-1 so all cards equal height */}
            <div className="p-4 flex-1 flex flex-col gap-3">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <h4 className="font-semibold text-gray-900 text-sm truncate group-hover:text-blue-600 transition-colors">
                            {b.name}
                        </h4>
                        <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                            {isGroupedCard ? (
                                <span className="inline-flex items-center gap-1">
                                    <Layers className="w-3 h-3" />
                                    {pagesCount} páginas agregadas
                                    {b.sentAt
                                        ? " · " + b.sentAt.slice(0, 10)
                                        : ""}
                                </span>
                            ) : (
                                <>
                                    {b.pageName || "—"}
                                    {b.sentAt && (
                                        <span className="text-gray-300 mx-1.5">
                                            ·
                                        </span>
                                    )}
                                    {b.sentAt && b.sentAt.slice(0, 10)}
                                </>
                            )}
                        </p>
                    </div>
                    <span className="text-[10px] font-mono text-gray-400 shrink-0 bg-gray-50 px-1.5 py-0.5 rounded">
                        {isGroupedCard ? `${pagesCount}×` : `#${b.id}`}
                    </span>
                </div>

                {/* Button previews — up to 2 */}
                {Array.isArray(b.buttons) && b.buttons.length > 0 && (
                    <div className="flex flex-col gap-1">
                        {b.buttons.slice(0, 2).map((btn, i) => (
                            <div
                                key={i}
                                className="text-center text-blue-600 text-[11px] font-semibold py-1.5 px-2 rounded-md bg-blue-50/50 border border-blue-100 truncate flex items-center justify-center gap-1.5"
                            >
                                {btn.type === "url" && (
                                    <ExternalLink className="w-3 h-3 opacity-60" />
                                )}
                                <span className="truncate">{btn.text}</span>
                            </div>
                        ))}
                        {b.buttons.length > 2 && (
                            <div className="text-center text-[10px] text-gray-400 font-mono">
                                + {b.buttons.length - 2} botões
                            </div>
                        )}
                    </div>
                )}

                <div
                    className={cn(
                        "grid gap-2 pt-3 border-t border-gray-100 mt-auto",
                        ctrVal != null ? "grid-cols-3" : "grid-cols-2"
                    )}
                >
                    <div>
                        <p className="text-[10px] text-gray-400 uppercase font-medium tracking-wider">
                            Enviadas
                        </p>
                        <p className="text-sm font-bold text-gray-900 font-mono">
                            {fmt(b.sends ?? 0)}
                        </p>
                    </div>
                    {ctrVal != null && (
                        <div>
                            <p className="text-[10px] text-gray-400 uppercase font-medium tracking-wider">
                                CTR
                            </p>
                            <span
                                className={cn(
                                    "inline-flex items-center text-[11px] font-bold px-1.5 py-0.5 rounded",
                                    ctrColor
                                )}
                            >
                                {ctrVal.toFixed(1) + "%"}
                            </span>
                        </div>
                    )}
                    <div>
                        <p className="text-[10px] text-gray-400 uppercase font-medium tracking-wider">
                            {isGroupedCard ? "Páginas" : "Botões"}
                        </p>
                        <p className="text-sm font-bold text-gray-900 font-mono">
                            {isGroupedCard
                                ? pagesCount
                                : b.buttonsCount ?? (b.buttons?.length ?? 0)}
                        </p>
                    </div>
                </div>
            </div>
        </button>
    );
}
